/* eslint-disable react/no-danger */
import React, { useContext, useEffect, useState } from 'react';
import DayJS from 'react-dayjs';
import { animateScroll as scroll } from 'react-scroll';
import { useParams } from 'react-router-dom';
import { getAuthClient } from '../../utils/auth';

import { getNormalizedApiData, getNormalizedApiDataByUuid } from '../../utils/ApiHelpers';
import {
  residentCommunityNormalizeData,
  residentFloorPlansNormalizeData,
  residentAmenitiesNormalizeData,
  residentMediaNormalizeData,
} from './PibPageNormalizedData';

import CommunityLogoWithLink from '../Community/CommunityLogoWithLink';
import CommunityContact from '../Community/CommunityContact';
import PanesWidget from '../PanesWidget/PanesWidget';
import FloorPlanDetail from '../FloorPlan/FloorPlanDetail';
import PlaceDetail from '../Place/PlaceDetail';
import SubpaneMediaDetail from '../PanesWidget/SubpaneMediaDetail';
import ConfigContext from '../../utils/ConfigContext/ConfigContext';
import FadingContainer from '../FadingContainer/FadingContainer';
import ResourceDetail from '../MyResources/ResourcesDetail';

import './PibPage.scss';

// get current year
const date = new Date();
const year = date.getFullYear();

const auth = new getAuthClient();

const PibPage = () => {
  const [ residentUuid, setResidentUuid ] = useState(null);
  const [ communityUuid, setCommunityUuid ] = useState( null );
  const [ residentCommunityData, setResidentCommunityData ] = useState( {} );
  const [ floorPlansData, setFloorPlansData ] = useState( null );
  const [ combinedFloorPlansData, setCombinedFloorPlansData ] = useState( null );
  const [ amenitiesData, setAmenitiesData ] = useState( null );
  const [ mediaData, setMediaData ] = useState( null );
  const [ panesData, setPanesData ] = useState( null );
  const [ myResourcesData, setMyResourcesData ] = useState( null );
  const [ soldOutFps, setSoldOutFps ] = useState( {} );

  const { communitySlug, drupalNid } = useParams();

  const { isAuthed, featureFlags } = useContext( ConfigContext );

  const FormattedStickyAmenitiesBlurb = ( rawAmenitiesBlurb ) => (
    <div className="pibPage-favorites__stickyContent">
      <div className="pibPage-favorites__stickyContent__title">Amenities & Services</div>
      <div className="pibPage-favorites__stickyContent__wysiwyg" dangerouslySetInnerHTML={{ __html: rawAmenitiesBlurb }} />
    </div>
  );

  const FormattedStickyNoFavoritesBlurb = (
    <div className="pibPage-favorites__stickyContent">
      <CommunityContact
        className="pibPage-favorites__stickyContent__text"
        pre={'You don’t have any favorites yet. If you think this is an error, contact '}
        post={'.'}
        communityData={residentCommunityData?.community}
        salesCounselorData={residentCommunityData?.salesCounselor}
        />
    </div>
  );

  const handleBackToTopClick = () => {
    scroll.scrollToTop( {
      duration: 500,
      smooth: 'easeInOutCubic',
    } );
  };

  // Get person and community info. We can go ahead and get My Resources here since
  // we are already loading the community.
  useEffect( () => {
    const fetchData = async () => {
      const includes = [
        'og_audience',
        'og_audience.field_logo',
        'og_audience.field_community_documents',
        'og_audience.field_community_documents.field_media_file',
        'field_spouse',
        'field_sales_counselor',
      ];
      const request = {
        entityType: 'node',
        contentType: 'resident',
        params: {
          include: includes.join( ',' ),
        },
        filter: {
          nid: drupalNid,
        }
      };
      const data = await getNormalizedApiData( request, null, residentCommunityNormalizeData );
      setResidentCommunityData( data );
      setCommunityUuid( data?.community?.uuid );
      setResidentUuid( data?.uuid);
      setMyResourcesData( data?.community?.resources);
    };
    if ( isAuthed && drupalNid ) {
      fetchData();
    }
  }, [isAuthed, drupalNid]);

  // Set page title.
  // Update page title with community name.
  const { community } = residentCommunityData || {};
  const { name } = community || {};
  useEffect( () => {
    document.title = `${name} | My YourTour`;
  }, [ name ] );

  // Get favorite floor plan data
  useEffect( () => {
    const fetchData = async () => {
      try {
        // Split into basic floor plan data first
        const basicIncludes = [
          'field_favorite_floor_plans',
          'field_favorite_floor_plans.field_2d_floor_plan',
          'field_favorite_floor_plans.field_3d_floor_plan',
          'field_favorite_floor_plans.field_level_of_care',
          'field_favorite_floor_plans.field_floor_plan_category',
          'field_favorite_floor_plans.field_fee_groups',
          'field_favorite_floor_plans.field_fee_groups.paragraph_type',
          'field_favorite_floor_plans.field_fee_groups.field_financial_plans',
          'field_favorite_floor_plans.field_finish_collection',
          'field_favorite_floor_plans.field_finish_collection.field_finish_option',
          'field_favorite_floor_plans.field_finish_collection.field_finish_option.field_finish_space',
          'field_favorite_floor_plans.field_finish_collection.field_finish_option.field_finish_palette',
          'field_favorite_floor_plans.field_finish_collection.field_finish_option.field_media_image',
          'field_favorite_floor_plans.field_virtual_tour',
          'field_favorite_floor_plans.field_virtual_tour.field_three_sixties',
          'field_favorite_floor_plans.field_finish_collection',
          'field_favorite_floor_plans.field_features',
          'field_favorite_floor_plans.field_photo_gallery',
          'field_favorite_floor_plans.field_photo_gallery.field_gallery_photos',
          'field_favorite_floor_plans.field_video',
          'field_favorite_floor_plans.field_downloadable_pdf',
        ];

        const basicRequest = {
          entityType: 'node',
          contentType: 'resident',
          uuid: residentUuid,
          params: {
            sort: 'title',
            include: basicIncludes.join( ',' ),
          },
        };

        // Get floor plan data with fee groups
        const basicData = await getNormalizedApiDataByUuid( basicRequest, residentFloorPlansNormalizeData);
        setFloorPlansData(basicData);
      } catch (error) {
        setFloorPlansData([]);
      }
    };

    if (isAuthed && residentUuid) {
      fetchData();
    }
  }, [isAuthed, residentUuid]);

  // Is the sold out feature enabled for this platform?
  const showSoldOut = featureFlags?.platformShowSoldOutStatus;

  // Get sold out fp data if we need it.
  useEffect(() => {
    const fetchData = async () => {
      const url = `/your_tour_api/floor_plan_availability/${communitySlug}/?_format=json`;
      const data = await auth.fetchWithAuthentication( url, 'GET', null );
      setSoldOutFps( data?.sold_out || [] );
    };
    if ( communitySlug && showSoldOut ) {
      fetchData();
    }
  }, [ communitySlug, showSoldOut ] );

  // Combine the floor plan data sets if we grabbed sold out data above.
  useEffect(() => {
    if (soldOutFps && floorPlansData) {
      const newFloorPlansData = floorPlansData.map((floorPlan) => {
        const combinedFloorPlan = floorPlan;
        combinedFloorPlan.remainingInventory = soldOutFps[floorPlan?.id]?.remaining_inventory;
        return combinedFloorPlan;
      });
      setCombinedFloorPlansData(newFloorPlansData);
    } else {
      setCombinedFloorPlansData(floorPlansData);
    }
  }, [floorPlansData,
    soldOutFps]);

  // Get favorite amenities data.
  useEffect(() => {
    const includes = [
      'field_favorite_amenities',
      'field_favorite_amenities.field_cover_photo',
      'field_favorite_amenities.field_virtual_tour',
      'field_favorite_amenities.field_virtual_tour.field_three_sixties',
      'field_favorite_amenities.field_video',
      'field_favorite_amenities.field_photo_gallery',
      'field_favorite_amenities.field_photo_gallery.field_gallery_photos',
    ];
    const request = {
      entityType: 'node',
      contentType: 'resident',
      uuid: residentUuid,
      params: {
        sort: 'title',
        include: includes.join( ',' ),
      },
    };
    const fetchData = async () => {
      const data = await getNormalizedApiDataByUuid( request, residentAmenitiesNormalizeData);
      setAmenitiesData(data);
    };
    if ( isAuthed && residentUuid ) {
      fetchData();
    }
  }, [ isAuthed, residentUuid ] );

  // Get favorite media data. We can't get the full entities from here becaue they have
  // different fields.
  useEffect(() => {
    const fetchData = async () => {
      const includes = [
        'field_favorite_media',
        'field_favorite_media.field_cover_photo',
        'field_favorite_media.field_gallery_photos',
        'field_favorite_media.field_three_sixties',
      ];
      // Now that we have the resident uuid, we can directly access the resident
      // instead of searching for it.
      const request = {
        entityType: 'node',
        contentType: 'resident',
        uuid: residentUuid,
        params: {
          sort: 'title',
          include: includes.join( ',' ),
        },
      };
      const data = await getNormalizedApiDataByUuid( request, residentMediaNormalizeData );
      setMediaData( data );
    };
    if ( isAuthed && residentUuid ) {
      fetchData();
    }
  }, [ isAuthed, residentUuid ] );

  useEffect( () => {
    const rawPanesData = [];

    if (combinedFloorPlansData?.length > 0) {
      const floorPlans = {
        id: 'floorPlans',
        btnText: 'My Favorite Floor Plans',
        paneData: combinedFloorPlansData,
        subpaneComponent:
          <FloorPlanDetail
            hasInternalHeader
            navClass="detailNav--centered pibPage-favorites__detailNav"
          />
        ,
        stickyMarkup: null,
      };
      rawPanesData.push(floorPlans);
    }

    const rawAmenitiesBlurb = residentCommunityData?.community?.amenitiesServicesHTML;
    if (rawAmenitiesBlurb || amenitiesData?.length > 0) {
      const stickyAmenitiesBlurb = FormattedStickyAmenitiesBlurb(rawAmenitiesBlurb);
      const amenities = {
        id: 'amenities',
        btnText: 'My Favorite Amenities',
        paneData: amenitiesData,
        subpaneComponent:
          <PlaceDetail
            hasInternalHeader
            navClass="detailNav--centered pibPage-favorites__detailNav"
          />
        ,
        stickyMarkup: stickyAmenitiesBlurb,
      };
      rawPanesData.push(amenities);
    }

    if (mediaData?.length > 0) {
      const media = {
        id: 'media',
        btnText: 'My Favorite Media',
        paneData: mediaData,
        subpaneComponent:
          <SubpaneMediaDetail
            navClass="detailNav--centered pibPage-favorites__detailNav"
          />
        ,
        stickyMarkup: null,
      };
      rawPanesData.push(media);
    }

    if ( myResourcesData?.links?.length || myResourcesData?.files?.length ) {
      // Panes is expecting an array of objects that each have an id, but
      // myResourcesData will always be a single object, so we have to create an array
      // with a single object in it.
      const resourcesPaneData = [{
        id: '1',
        ...myResourcesData,
      }];
      const resources = {
        id: 'resources',
        btnText: 'My Resources',
        // Panes is expecting an array of objects that each have an id, but
        // myResourcesData will always be a single object.
        paneData: resourcesPaneData,
        subpaneComponent: <ResourceDetail />,
        stickyMarkup: null,
      };
      rawPanesData.push(resources);
    }

    // The person has no favorites yet
    // and the community has no amenities and services blurb.
    if (floorPlansData?.length === 0
      && amenitiesData?.length === 0
      && mediaData?.length === 0
      && !rawAmenitiesBlurb
    ) {
      const amenities = {
        id: 'amenities',
        btnText: 'My Favorite Amenities',
        paneData: [],
        subpaneComponent:
          <PlaceDetail
            hasInternalHeader
            navClass="detailNav--centered pibPage-favorites__detailNav"
          />
        ,
        stickyMarkup: FormattedStickyNoFavoritesBlurb,
      };
      rawPanesData.push(amenities);
    }

    setPanesData(rawPanesData);
  }, [
    residentCommunityData,
    combinedFloorPlansData,
    amenitiesData,
    myResourcesData,
    mediaData,
  ] );

  return (
    <FadingContainer>
      <div className="pibPage">

        <div className="pibPage-branding">
          <CommunityLogoWithLink
            className="pibPage-branding__logoLink"
            communityData={residentCommunityData?.community}
          />
        </div>

        <div className="pibPage-welcome">

          <div className="pibPage-welcome__intro">
            {residentCommunityData?.names
              && (
                <>
                  {residentCommunityData.names}
                  {', '}
                  {residentCommunityData?.community?.greeting || 'Your Personalized Interactive Brochure Is Ready.'}
                </>
              )}
          </div>

          <CommunityContact
            className="pibPage-welcome__contact"
            communityData={residentCommunityData?.community}
            salesCounselorData={residentCommunityData?.salesCounselor}
          />

          <div className="pibPage-welcome__date">
            Personalized
            {' '}
            <DayJS element="span" format="MMMM D[,] YYYY">{residentCommunityData?.createdDate}</DayJS>
            {' '}
            — Last edited
            {' '}
            <DayJS element="span" format="MMMM D[,] YYYY">{residentCommunityData?.changedDate}</DayJS>
          </div>

        </div>

        {residentCommunityData?.message && (
          <div className="pibPage-message">
            <div className="pibPage-message__body" dangerouslySetInnerHTML={{ __html: residentCommunityData.message }} />
          </div>
        )}

        <div className="pibPage-favorites">
          <PanesWidget panesData={panesData} />
        </div>

        <div className="pibPage-backToTop">
          <button type="button" className="pibPage-backToTop__button" onClick={handleBackToTopClick}>
            <span className="pibPage-backToTop__button__arrow" />
            <span className="pibPage-backToTop__button__text">Back to Top</span>
          </button>
        </div>

        <div className="pibPage-vectre">
          <div className="pibPage-vectre__message">
            Your Personalized Interactive Brochure is brought to you by
            {' '}
            <span>YOUR</span>
            TOUR, a sales enablement tool by THE VECTRE.
          </div>
          <div className="pibPage-vectre__colophon">
            Copyright ©
            {' '}
            {year}
            {' '}
            The Vectre - All Rights Reserved
          </div>
        </div>

      </div>
    </FadingContainer>
  );
};

export default PibPage;
